import type { FunctionalComponent } from 'vue'
import {
  BeakerIcon,
  ChatBubbleBottomCenterIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  FlagIcon,
  FunnelIcon,
  GiftIcon,
  GlobeAltIcon,
  LinkIcon,
  MegaphoneIcon,
  PuzzlePieceIcon,
  UserGroupIcon,
  VariableIcon
} from '@heroicons/vue/24/outline'
import type { ConfigChange } from '@dashboard/composables/useConfigChanges'
import type { PublishNoteChange } from '@dashboard/modules/righty/composables/useRightyHeadless'
import { phraseSegments } from '@dashboard/services/phraseText'
import type { PhraseSegment } from '@dashboard/services/phraseText'
import { describeChangeFields, describeHostedFields } from '@dashboard/services/publishFieldVocabulary'
import type { Config } from '@dashboard/types/config'

export type ChangeKind = PublishNoteChange['change']

export interface ReviewField {
  field: PhraseSegment[]
  before: PhraseSegment[]
  after: PhraseSegment[]
}

export interface ReviewItem {
  key: string
  kind: ChangeKind
  name: PhraseSegment[]
  fields: ReviewField[]
  change: ConfigChange
}

export interface ReviewGroup {
  key: string
  label: string
  icon: FunctionalComponent
  summary: string
  items: ReviewItem[]
}

interface GroupDefinition {
  key: string
  label: string
  icon: FunctionalComponent
  categories: string[]
}

// Display order; every analyzer category belongs to exactly one group, and any
// category not listed here falls into Settings so no change is ever hidden.
const GROUPS: GroupDefinition[] = [
  { key: 'pages', label: 'Pages', icon: DocumentTextIcon, categories: ['Pages', 'Site styles', 'Project scripts'] },
  { key: 'offers', label: 'Offers & forms', icon: GiftIcon, categories: ['Offers / Forms'] },
  { key: 'flows', label: 'Flows', icon: FunnelIcon, categories: ['Flows'] },
  { key: 'widgets', label: 'Widgets', icon: PuzzlePieceIcon, categories: ['Widgets'] },
  { key: 'campaigns', label: 'Campaigns', icon: MegaphoneIcon, categories: ['Campaigns'] },
  { key: 'phrases', label: 'Phrases', icon: VariableIcon, categories: ['Phrases'] },
  { key: 'questions', label: 'Questions', icon: ChatBubbleBottomCenterIcon, categories: ['Questions'] },
  { key: 'segments', label: 'Segment groups', icon: UserGroupIcon, categories: ['Segment Groups'] },
  { key: 'goals', label: 'Goals', icon: FlagIcon, categories: ['Goals'] },
  { key: 'splitTests', label: 'Split tests', icon: BeakerIcon, categories: ['Split Tests'] },
  { key: 'integrations', label: 'Integrations', icon: LinkIcon, categories: ['Integrations'] },
  { key: 'domains', label: 'Domains', icon: GlobeAltIcon, categories: ['Domains'] },
  { key: 'settings', label: 'Settings', icon: Cog6ToothIcon, categories: [] }
]

const KIND: Record<ConfigChange['type'], ChangeKind> = { added: 'added', modified: 'edited', deleted: 'removed' }
const KIND_ORDER: ChangeKind[] = ['added', 'edited', 'removed']

/** "12 added", "1 added · 1 edited" */
export function kindSummary(items: readonly { kind: ChangeKind }[]): string {
  return KIND_ORDER.flatMap((kind) => {
    const count = items.filter((item) => item.kind === kind).length
    return count ? [`${count} ${kind}`] : []
  }).join(' · ')
}

export function buildReviewGroups(
  changes: readonly ConfigChange[],
  current: Config | null,
  published: Config | null
): ReviewGroup[] {
  const phrases = [current?.phrases ?? [], published?.phrases ?? []]
  // Old values read with the published phrases, new values with the draft's.
  const publishedPhrases = [published?.phrases ?? [], current?.phrases ?? []]
  const settings = GROUPS.find((group) => group.key === 'settings')
  const buckets = new Map<GroupDefinition, ReviewItem[]>()
  changes.forEach((change, index) => {
    const group = GROUPS.find((definition) => definition.categories.includes(change.category)) ?? settings
    if (!group) {
      return
    }
    const items = buckets.get(group) ?? []
    items.push({
      key: `${change.category}:${change.id}:${index}`,
      kind: KIND[change.type],
      name: phraseSegments(change.name, ...phrases),
      fields: (change.hosted
        ? describeHostedFields(change.hosted)
        : current
          ? describeChangeFields(change, current, published)
          : []
      ).map((field) => ({
        field: phraseSegments(field.field, ...phrases),
        before: field.before === null ? [] : phraseSegments(field.before, ...publishedPhrases),
        after: field.after === null ? [] : phraseSegments(field.after, ...phrases)
      })),
      change
    })
    buckets.set(group, items)
  })

  return GROUPS.flatMap((group) => {
    const items = buckets.get(group)
    return items ? [{ key: group.key, label: group.label, icon: group.icon, summary: kindSummary(items), items }] : []
  })
}

const NOTE_VALUE_LIMIT = 120

function plain(segments: PhraseSegment[]): string | null {
  const text = segments.map((segment) => segment.text).join('')
  if (!text) {
    return null
  }
  return text.length > NOTE_VALUE_LIMIT ? `${text.slice(0, NOTE_VALUE_LIMIT - 1)}…` : text
}

/** The change list exactly as the dialog shows it, in plain text, for Righty's note. */
export function publishNoteChanges(groups: readonly ReviewGroup[]): PublishNoteChange[] {
  return groups.flatMap((group) =>
    group.items.map((item) => ({
      group: group.label,
      change: item.kind,
      name: plain(item.name) ?? group.label,
      ...(item.fields.length
        ? {
            fields: item.fields.map((field) => ({
              field: field.field.map((segment) => segment.text).join(''),
              before: plain(field.before),
              after: plain(field.after)
            }))
          }
        : {})
    }))
  )
}

/** Deterministic note used until (or instead of) Righty's draft. */
export function fallbackPublishNote(groups: readonly ReviewGroup[]): string {
  const parts = groups.map((group) => `${group.label.toLowerCase()} (${kindSummary(group.items)})`)
  if (parts.length === 0) {
    return ''
  }
  const list = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}`
  return `Publish changes to ${list}.`
}
